import { useState, type FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d9vbkkcpc8jh8d0bvqkg';

type FormStatus = {
  type: 'idle' | 'success' | 'error';
  message: string;
};

const messageTypes = ['意见反馈', '合作洽谈', '侵权投诉', '其他'];

export default function ContactForm() {
  const [status, setStatus] = useState<FormStatus>({ type: 'idle', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // honeypot 反垃圾校验
    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus({
        type: 'success',
        message: '提交成功！感谢您的留言，我们会尽快与您联系。',
      });
      form.reset();
      return;
    }
    formData.delete('website_alt');

    setSubmitting(true);
    setStatus({ type: 'idle', message: '' });

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        payload.append(key, String(value));
      });

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: payload.toString(),
      });

      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = (parsed?.meta as Record<string, unknown>) || {};
      const serverMsg =
        String(meta?.message || parsed?.message || meta?.detail || '') ||
        responseText;

      if (res.ok && parsed?.code === 'OK') {
        setStatus({
          type: 'success',
          message: '提交成功！感谢您的留言，我们会尽快与您联系。',
        });
        form.reset();
      } else {
        const errorMsg = serverMsg.toLowerCase().includes('spam')
          ? '提交内容未通过验证，请检查后重试。'
          : serverMsg || '提交失败，请稍后重试。';
        setStatus({ type: 'error', message: errorMsg });
      }
    } catch {
      setStatus({ type: 'error', message: '网络异常，提交失败，请稍后重试。' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      id="contact-form"
      data-readdy-form="联系我们表单"
      onSubmit={handleSubmit}
      className="rounded-2xl bg-background-50 p-5 md:p-6"
      noValidate={false}
    >
      <h3 className="flex items-center gap-2 text-base font-semibold text-foreground-900">
        <i className="ri-chat-3-fill text-primary-500"></i>
        给我们留言
      </h3>
      <p className="mt-1 text-xs text-foreground-500">
        留下您的想法与联系方式，我们会在 24 小时内回复
      </p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm text-foreground-700">
            您的称呼
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="怎么称呼您"
            className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm text-foreground-700">
            联系邮箱
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="subject" className="mb-1.5 block text-sm text-foreground-700">
          留言类型
        </label>
        <select
          id="subject"
          name="subject"
          className="h-11 w-full rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-300"
        >
          {messageTypes.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between">
          <label htmlFor="message" className="block text-sm text-foreground-700">
            留言内容
          </label>
          <span className="text-xs text-foreground-400">最多 500 字</span>
        </div>
        <textarea
          id="message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="想对布丁漫画网说的话……"
          className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm leading-6 text-foreground-800 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
        ></textarea>
      </div>

      {/* honeypot 反垃圾字段 */}
      <input
        type="text"
        name="website_alt"
        id="website_alt"
        autoComplete="off"
        tabIndex={-1}
        aria-hidden="true"
        readOnly
        className="hp-field"
      />

      <button
        type="submit"
        disabled={submitting}
        className="mt-5 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            提交中……
          </>
        ) : (
          <>
            <i className="ri-send-plane-fill"></i>
            提交留言
          </>
        )}
      </button>

      {status.message && (
        <div
          role="status"
          className={`mt-4 rounded-lg px-4 py-3 text-sm ${
            status.type === 'success'
              ? 'bg-secondary-100 text-secondary-900'
              : status.type === 'error'
                ? 'bg-accent-100 text-accent-900'
                : ''
          }`}
        >
          <i
            className={`mr-1.5 ${
              status.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'
            }`}
          ></i>
          {status.message}
        </div>
      )}
    </form>
  );
}